// Reusable academic-progression natural-sort utility for Education360.
// Sorts classes from LOWEST academic level → HIGHEST, grouping streams
// (A/B/C…) inside their parent level. Unknown levels sort AFTER recognised
// levels using natural (case-insensitive) sort. Does not fabricate, rename
// or mutate class records; operates only on the display order.
//
// Recognised naming variants:
//   ECD A / ECDA / ECD-A
//   Grade 1 / Grade1 / Gr1 / G1
//   Form 1A / Form1A / F1A
//   Lower Six / Lower Sixth / L6
//   Upper Six / Upper Sixth / U6
//
// Anything unrecognised keeps its display name and is naturally sorted at
// the end (rank Number.POSITIVE_INFINITY − epsilon).

const LEVEL_ORDER = [
  { rank: 0,  test: /^\s*ECD\s*A\b/i,                    label: "ECD A" },
  { rank: 1,  test: /^\s*ECD\s*B\b/i,                    label: "ECD B" },
  { rank: 2,  test: /^\s*ECD\b/i,                        label: "ECD" },
  { rank: 10, test: /^\s*(?:Grade|Gr|G)\s*1\b/i,         label: "Grade 1" },
  { rank: 11, test: /^\s*(?:Grade|Gr|G)\s*2\b/i,         label: "Grade 2" },
  { rank: 12, test: /^\s*(?:Grade|Gr|G)\s*3\b/i,         label: "Grade 3" },
  { rank: 13, test: /^\s*(?:Grade|Gr|G)\s*4\b/i,         label: "Grade 4" },
  { rank: 14, test: /^\s*(?:Grade|Gr|G)\s*5\b/i,         label: "Grade 5" },
  { rank: 15, test: /^\s*(?:Grade|Gr|G)\s*6\b/i,         label: "Grade 6" },
  { rank: 16, test: /^\s*(?:Grade|Gr|G)\s*7\b/i,         label: "Grade 7" },
  { rank: 20, test: /^\s*(?:Form|F)\s*1\b/i,             label: "Form 1" },
  { rank: 21, test: /^\s*(?:Form|F)\s*2\b/i,             label: "Form 2" },
  { rank: 22, test: /^\s*(?:Form|F)\s*3\b/i,             label: "Form 3" },
  { rank: 23, test: /^\s*(?:Form|F)\s*4\b/i,             label: "Form 4" },
  // Year 10 → Form 4 equivalence is authoritatively defined in the seeder
  // constants (`seeders/students.py`: UK_TO_ZW["Year 10"] = "Form 4"). Edwin
  // Thom's Year 10 Windsor class is preserved by design; we only sort it into
  // its authoritative curriculum-stage position here (display-only).
  { rank: 23, test: /^\s*Year\s*10\b/i,                  label: "Year 10 (Form 4 equivalent)" },
  { rank: 30, test: /^\s*(?:Lower\s*Six(?:th)?|L6)\b/i,  label: "Lower Six" },
  { rank: 31, test: /^\s*(?:Upper\s*Six(?:th)?|U6)\b/i,  label: "Upper Six" },
];

// Extract the trailing stream letter (A/B/C…) so streams group inside a level.
function streamKey(name) {
  const m = String(name || "").match(/(?:^|\s|-)([A-Z])\s*$/);
  return m ? m[1].toUpperCase() : "";
}

function levelRank(name) {
  const s = String(name || "");
  for (const lvl of LEVEL_ORDER) if (lvl.test.test(s)) return lvl.rank;
  return Number.MAX_SAFE_INTEGER - 1;
}

function naturalCollator() {
  return new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
}

// Public API — deterministic academic-progression sort. Returns a new array,
// does not mutate the input.
export function sortByAcademicProgression(classes) {
  if (!Array.isArray(classes)) return classes;
  const col = naturalCollator();
  return [...classes].sort((a, b) => {
    const an = a?.name ?? "";
    const bn = b?.name ?? "";
    const ar = levelRank(an);
    const br = levelRank(bn);
    if (ar !== br) return ar - br;
    // Within the same level, group by stream letter then natural-sort name.
    const as = streamKey(an);
    const bs = streamKey(bn);
    if (as !== bs) return col.compare(as, bs);
    return col.compare(an, bn);
  });
}

export default sortByAcademicProgression;
